import { AStockSymbolWhite, BaseSymbol } from "@/api/model";
import dayjs from "dayjs";
import { defineStore, Store, StoreDefinition, storeToRefs } from "pinia";
import { useMainStore } from ".";
import i18n from "@/locales/index";
import { ASTOCK_LIST } from "@/locales/lang/astock";
import { $getSymbolsAstockName } from "@/api/base";

const t = i18n.global.t;

/** 用户资产分析页（asset.vue）筛选条件 Cookie，按登录用户 username 区分 */
const ASSET_PAGE_FILTER_COOKIE_PREFIX = "tx_asset_filter_v1_";
const ASSET_PAGE_FILTER_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 400;

export type AssetPageTimeType = "hour" | "day" | "week" | "month";

/** v=1 JSON 存入单条 Cookie */
export interface AssetPageFilterPrefsV1 {
  v: 1;
  accountTypeSelect?: string;
  sourceAssetSelect?: string;
  apiSelect?: number;
  timeType?: AssetPageTimeType;
  /** 对应 asset.vue 中 TimeSelect 枚举数值 */
  timeSelect?: number;
  timeRangeStartMs?: number;
  timeRangeEndMs?: number;
}

function assetPageFilterCookieName(userKey: string): string {
  return `${ASSET_PAGE_FILTER_COOKIE_PREFIX}${encodeURIComponent(userKey)}`;
}

function parseDocumentCookieValue(cookie: string, name: string): string | null {
  const parts = cookie.split("; ");
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    const idx = p.indexOf("=");
    if (idx === -1) {
      continue;
    }
    const k = p.slice(0, idx);
    if (k === name) {
      return decodeURIComponent(p.slice(idx + 1));
    }
  }
  return null;
}

function isAssetPageTimeType(v: unknown): v is AssetPageTimeType {
  return v === "hour" || v === "day" || v === "week" || v === "month";
}

/** 从 Cookie 读取资产页筛选；无数据或解析失败返回 null */
export function readAssetPageFilterPrefs(
  userKey: string
): Partial<AssetPageFilterPrefsV1> | null {
  if (typeof document === "undefined" || !userKey) {
    return null;
  }
  const raw = parseDocumentCookieValue(
    document.cookie,
    assetPageFilterCookieName(userKey)
  );
  if (raw == null || raw === "") {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }
    const o = parsed as Record<string, unknown>;
    if (o.v !== 1) {
      return null;
    }
    const out: Partial<AssetPageFilterPrefsV1> = { v: 1 };
    if (typeof o.accountTypeSelect === "string") {
      out.accountTypeSelect = o.accountTypeSelect;
    }
    if (typeof o.sourceAssetSelect === "string") {
      out.sourceAssetSelect = o.sourceAssetSelect;
    }
    if (typeof o.apiSelect === "number" && Number.isFinite(o.apiSelect)) {
      out.apiSelect = o.apiSelect;
    }
    if (isAssetPageTimeType(o.timeType)) {
      out.timeType = o.timeType;
    }
    if (typeof o.timeSelect === "number" && Number.isFinite(o.timeSelect)) {
      out.timeSelect = o.timeSelect;
    }
    if (
      typeof o.timeRangeStartMs === "number" &&
      Number.isFinite(o.timeRangeStartMs)
    ) {
      out.timeRangeStartMs = o.timeRangeStartMs;
    }
    if (
      typeof o.timeRangeEndMs === "number" &&
      Number.isFinite(o.timeRangeEndMs)
    ) {
      out.timeRangeEndMs = o.timeRangeEndMs;
    }
    return out;
  } catch {
    return null;
  }
}

/** 将资产页筛选写入 Cookie（整包覆盖） */
export function writeAssetPageFilterPrefs(
  userKey: string,
  prefs: AssetPageFilterPrefsV1
): void {
  if (typeof document === "undefined" || !userKey) {
    return;
  }
  const name = assetPageFilterCookieName(userKey);
  const payload = encodeURIComponent(JSON.stringify(prefs));
  document.cookie = `${name}=${payload}; path=/; max-age=${ASSET_PAGE_FILTER_COOKIE_MAX_AGE_SEC}; samesite=lax`;
}

export interface DashBoardTab {
  title: string;
  key: string;
  value: BaseSymbol;
  closable: boolean;
}

export const useDashBoardStore = defineStore("dashboard", {
  persist: true,
  state: () => {
    return <
      {
        currentDashBoard: string;
        dashBoardList: DashBoardTab[];
        searchSymbolList: BaseSymbol[];
        symbolsAI?: BaseSymbol[];
        NowChart: string;
        NowChartSaveLoadAdapter: string;
        currentCommandTab: string;
        currentPositionAssetApiIdList: number[];
        NowSyncMode: boolean;
        NowQuickMode: boolean;
        NowAffixedIntervals: [];
        spotLeverageActivate: boolean;
        spotLeverageAutoLoan: boolean;
        astockNameMap: Map<string, AStockSymbolWhite>;

        NowCurrencyType: number; //当前手动交易币种类型 0为自动，1为币，2为U，3为张数

        NowSyncRatio?: number; //同步比例
        AiLeftSelectDisplay: boolean; //AI左选择框显示
        AiLeftSelectAiSelect: boolean; //AI左选择框是否为AI选择

        ExtendChartMap: Record<string, string>;
      }
    >{
      currentDashBoard: "BINANCE/BTCUSDT/SPOT",
      dashBoardList: [
        {
          title: "BINANCE/BTCUSDT-" + t("dashboardTrade.Spot"),
          key: "BINANCE/BTCUSDT/SPOT",
          value: {
            exchange: "BINANCE",
            currency: "BTC",
            valuation: "USDT",
            account_type: "SPOT",
            symbol: "BTCUSDT",
          },
          closable: true,
        },
        {
          title: "OKX/BTC-USDT-" + t("dashboardTrade.Spot"),
          key: "OKX/BTC-USDT/SPOT",
          value: {
            exchange: "OKX",
            currency: "BTC",
            valuation: "USDT",
            account_type: "SPOT",
            symbol: "BTC-USDT",
          },
          closable: true,
        },
        {
          title: "BYBIT/BTCUSDT-" + t("dashboardTrade.Spot"),
          key: "BYBIT/BTCUSDT/spot",
          value: {
            exchange: "BYBIT",
            currency: "BTC",
            valuation: "USDT",
            account_type: "spot",
            symbol: "BTCUSDT",
          },
          closable: true,
        },
      ],
      searchSymbolList: [],
      symbolsAI: undefined,
      NowChart: "",
      NowChartSaveLoadAdapter: "",
      currentCommandTab: "current",
      currentPositionAssetApiIdList: [],
      NowSyncMode: true,
      NowQuickMode: true,
      NowAffixedIntervals: [],
      spotLeverageActivate: false,
      spotLeverageAutoLoan: false,

      astockNameMap: new Map<string, AStockSymbolWhite>(),

      NowCurrencyType: 0,
      NowSyncRatio: 100,
      AiLeftSelectDisplay: false,
      AiLeftSelectAiSelect: false,

      ExtendChartMap: {},
    };
  },

  actions: {
    addDashBoard(symbol: string) {
      let hasSymbol = false;
      const mainStore = useMainStore();
      this.dashBoardList.forEach((value) => {
        if (value.key == symbol) {
          hasSymbol = true;
          return;
        }
      });

      if (!hasSymbol) {
        const s = mainStore.getSymbolByKey(symbol);
        this.dashBoardList.push({
          title: this.getTitleByTabId(symbol),
          key: symbol,
          value: s,
          closable: true,
        });
      }
      this.currentDashBoard = symbol;
    },
    removeDashBoard(symbol: string) {
      let index = -1;
      this.dashBoardList.forEach((v, i) => {
        if (v.key == symbol) {
          index = i;
          return;
        }
      });
      if (index > -1) {
        this.dashBoardList.splice(index, 1);
        if (this.currentDashBoard === symbol) {
          if (index > 0) {
            this.currentDashBoard = this.dashBoardList[index - 1].key;
          } else {
            this.currentDashBoard = this.dashBoardList[0].key;
          }
        }
      }
    },
    replaceDashBorad(oldSymbol: string, newSymbol: string) {
      let index = -1;
      this.dashBoardList.forEach((v, i) => {
        if (v.key == oldSymbol) {
          index = i;
          return;
        }
      });
      const mainStore = useMainStore();
      const s = mainStore.getSymbolByKey(newSymbol);
      this.dashBoardList[index] = {
        title: this.getTitleByTabId(newSymbol),
        key: newSymbol,
        value: s,
        closable: true,
      };
      this.currentDashBoard = this.dashBoardList[index].key;
    },
    initSearchSymbolList() {
      const mainStore = useMainStore();
      const { symbols } = storeToRefs(mainStore);
      if (symbols.value.length == 0) {
        setTimeout(() => {
          this.initSearchSymbolList();
        }, 1000);
        return;
      }
      const newList: BaseSymbol[] = [];
      symbols.value.forEach((v) => {
        newList.push(v);
      });
      const newSortList = newList.sort((a, b) => {
        if (a.symbol > b.symbol) {
          return 1;
        } else {
          return -1;
        }
      });
      this.searchSymbolList = newSortList;
    },
    searchSymbol(text: string, acType: string) {
      const mainStore = useMainStore();
      const { symbols } = storeToRefs(mainStore);
      const newList: BaseSymbol[] = [];
      symbols.value.forEach((v) => {
        if (
          v.symbol.indexOf(text) != -1 ||
          v.symbol.indexOf(text.toUpperCase()) != -1
        ) {
          newList.push(v);
        }
      });
      this.searchSymbolList = newList;
    },
    getTitleByTabId(tab_id: string) {
      const split = tab_id.split("/");
      // console.log(split);
      const exchange = split[0];
      const symbol = split[1];
      const account_type = split[2];
      // console.log(tab_id);
      switch (exchange) {
        case "BINANCE":
          switch (account_type) {
            case "SPOT":
              return `${exchange}/${symbol} - ${t("dashboardTrade.Spot")}`;
            case "FUTURE": {
              const futureSplit = symbol.split("_");
              if (futureSplit.length == 2) {
                //当季或者次季
                const time = dayjs(futureSplit[1], "YYMMDD");
                if (time.subtract(90, "day").unix() <= dayjs().unix()) {
                  //example: BINANCE/BTCUSD-当季0628
                  return `${exchange}/${futureSplit[0]} - ${t(
                    "dashboardTrade.Currently"
                  )}${time.format("MMDD")}`;
                } else {
                  //example: BINANCE/BTCUSD-次季0927
                  return `${exchange}/${futureSplit[0]} - ${t(
                    "dashboardTrade.Quarterly"
                  )}${time.format("MMDD")}`;
                }
              }
              return `${exchange}/${symbol} - ${t("dashboardTrade.Perpetual")}`;
            }
            case "SWAP": {
              const swapSplit = symbol.split("_");
              if (swapSplit[1] == "PERP") {
                return `${exchange}/${swapSplit[0]} - ${t(
                  "dashboardTrade.Perpetual"
                )}`;
              } else {
                const time = dayjs(swapSplit[1], "YYMMDD");
                if (time.subtract(90, "day").unix() <= dayjs().unix()) {
                  //example: BINANCE/BTCUSD-当季0628
                  return `${exchange}/${swapSplit[0]} - ${t(
                    "dashboardTrade.Currently"
                  )}${time.format("MMDD")}`;
                } else {
                  //example: BINANCE/BTCUSD-次季0927
                  return `${exchange}/${swapSplit[0]} - ${t(
                    "dashboardTrade.Quarterly"
                  )}${time.format("MMDD")}`;
                }
              }
            }
          }
          break;
        case "OKX":
          switch (account_type) {
            case "SPOT": {
              const split = symbol.split("-");
              return `${exchange}/${split[0]}-${split[1]} - ${t(
                "OkxAccountType.SPOT"
              )}`;
            }
            case "SWAP": {
              const split = symbol.split("-");
              return `${exchange}/${split[0]}-${split[1]} - ${t(
                "OkxAccountType.SWAP"
              )}`;
            }
            case "FUTURES": {
              const split = symbol.split("-");
              return `${exchange}/${symbol}`;
            }
          }
          break;
        case "BYBIT":
          switch (account_type) {
            case "spot": {
              return `${exchange}/${symbol} - ${t("OkxAccountType.SPOT")}`;
            }
            case "linear": {
              const split = symbol.split("-");
              if (split.length == 2) {
                return `${exchange}/${symbol}`;
              } else {
                return `${exchange}/${symbol} - ${t(
                  "BybitAccountType.Perpetual"
                )}`;
              }
            }
            case "inverse": {
              return `${exchange}/${symbol} - ${t("BybitAccountType.Inverse")}`;
            }
          }
          break;
        case "BITGET":
          switch (account_type) {
            case "SPOT": {
              return `${exchange}/${symbol} - ${t("dashboardTrade.Spot")}`;
            }
            case "USDT-FUTURES":
            case "USDC-FUTURES": {
              return `${exchange}/${symbol} - ${t(
                "dashboardTrade.USDFutures"
              )}`;
            }
            case "COIN-FUTURES": {
              return `${exchange}/${symbol} - ${t(
                "dashboardTrade.COINFutures"
              )}`;
            }
          }
          break;
        case "GATE":
          switch (account_type) {
            case "spot": {
              return `${exchange}/${symbol} - ${t("GateAccountType.SPOT")}`;
            }
            case "futures": {
              return `${exchange}/${symbol} - ${t("GateAccountType.FUTURES")}`;
            }
            case "delivery": {
              return `${exchange}/${symbol}`;
            }
          }
          break;
        case "DERIBIT":
          return `${exchange}/${symbol.split("-")[0]} - ${t(
            "Dictionary.OPTION"
          )}`;
        case "TRADINGX":
          switch (account_type) {
            case "index":
              return `${exchange}/${symbol} - ${t("dashboardTrade.Index")}`;
            case "spot":
              return `${exchange}/${symbol} - ${t("dashboardTrade.Spot")}`;
            case "futures":
              return `${exchange}/${symbol} - ${t("dashboardTrade.Futures")}`;
          }
          break;
        case "forex":
          return `${exchange}/${symbol.split(".")[0]} - ${t(
            "dashboardTrade.Forex"
          )}`;
        case "futuresmkt":
        case "FUTURESMKT":
          return `${exchange}/${symbol} - ${t(
            "components_tradeheader.futuresmktPanel.futuresTag"
          )}`;
        case "Astock":
          return `${t("AstockSymbol.A_" + symbol.replaceAll(".", "_"))} - ${t(
            "dashboardTrade.AStock"
          )}`;
        case "1":
          // console.log(
          //   `${t("AstockSymbol.A_" + symbol)} - ${t("dashboardTrade.AStock")}`
          // );
          if (ASTOCK_LIST[`A_${account_type}_${symbol}`] == undefined) {
            const astockSymbolWhite = (
              this.astockNameMap as Map<string, AStockSymbolWhite>
            ).get(tab_id);
            let targetAccountType = "SH";
            if (account_type == "2") {
              targetAccountType = "SZ";
            }
            // console.log(astockSymbolWhite);
            if (astockSymbolWhite != undefined) {
              return `${exchange}/${targetAccountType}_${symbol}_${
                astockSymbolWhite.security_name
              } - ${t("dashboardTrade.AStock")}`;
            } else {
              return (
                `${exchange}/${targetAccountType}_` +
                symbol +
                ` - ${t("dashboardTrade.AStock")}`
              );
            }
          }
          return `${exchange}/${t(
            "AstockSymbol.A_" + account_type + "_" + symbol
          )} - ${t("dashboardTrade.AStock")}`;
        case "ASTER":
          switch (account_type) {
            case "SPOT":
              return `${exchange}/${symbol} - ${t("dashboardTrade.Spot")}`;
            case "FUTURE": {
              const futureSplit = symbol.split("_");
              if (futureSplit.length == 2) {
                //当季或者次季
                const time = dayjs(futureSplit[1], "YYMMDD");
                if (time.subtract(90, "day").unix() <= dayjs().unix()) {
                  //example: BINANCE/BTCUSD-当季0628
                  return `${exchange}/${futureSplit[0]} - ${t(
                    "dashboardTrade.Currently"
                  )}${time.format("MMDD")}`;
                } else {
                  //example: BINANCE/BTCUSD-次季0927
                  return `${exchange}/${futureSplit[0]} - ${t(
                    "dashboardTrade.Quarterly"
                  )}${time.format("MMDD")}`;
                }
              }
              return `${exchange}/${symbol} - ${t("dashboardTrade.Perpetual")}`;
            }
          }
          break;
        case "SUNX":
          switch (account_type) {
            case "swap":
              return `${exchange}/${symbol} - ${t("SunxAccountType.SWAP")}`;
          }
          break;
        case "XCOIN":
          switch (account_type) {
            case "spot": {
              return `${exchange}/${symbol} - ${t("XcoinAccountType.SPOT")}`;
            }
            case "linear_perpetual": {
              return `${exchange}/${symbol} - ${t(
                "XcoinAccountType.LINEAR_PERPETUAL"
              )}`;
            }
            case "linear_futures": {
              return `${exchange}/${symbol} - ${t(
                "XcoinAccountType.LINEAR_FUTURES"
              )}`;
            }
          }
          break;
        default:
          return "";
      }
      return "";
    },
    /**
     * 根据当前 userApiMap 剔除已删除/不存在的 api_id，同步 currentPositionAssetApiIdList 与 user_config 中的 api_select_list。
     * 在 refreshApiMap 成功后调用；依赖 App.vue 中对 currentPositionAssetApiIdList 的 watch 写回后端。
     */
    reconcileApiSelectListWithUserApiMap() {
      const mainStore = useMainStore();
      if (mainStore.user == undefined) {
        return;
      }
      const valid = mainStore.userApiMap;
      const cfgList = mainStore.userConfigData?.api_select_list;
      const raw =
        cfgList !== undefined
          ? cfgList
          : [...this.currentPositionAssetApiIdList];
      const next = raw.filter((id) => valid.has(id));

      const unchanged =
        next.length === raw.length && next.every((id, idx) => id === raw[idx]);
      if (unchanged) {
        return;
      }
      if (mainStore.userConfigData != undefined) {
        mainStore.userConfigData.api_select_list = next;
      }
      this.currentPositionAssetApiIdList = next;
    },
    refreshAstockNameMap() {
      $getSymbolsAstockName({}).then((res) => {
        if (res.code == 200) {
          const map = new Map<string, AStockSymbolWhite>();
          const dataList = res.data as AStockSymbolWhite[];
          for (let i = 0; i < dataList.length; i++) {
            map.set(
              ["1", dataList[i].security_id, dataList[i].exchange_id].join("/"),
              dataList[i]
            );
          }
          this.astockNameMap = map;
          // console.log(this.astockNameMap, "aaaaaaaaa");
        }
      });
    },
    setSymbolsAI(symbolsAI: BaseSymbol[] | undefined) {
      this.symbolsAI = symbolsAI;
    },
    getSymbolsAI() {
      return this.symbolsAI;
    },
    setCurrentCommandTab(tab: string) {
      this.currentCommandTab = tab;
    },
    setSyncMode(syncMode: boolean) {
      this.NowSyncMode = syncMode;
    },
    setQuickMode(quickMode: boolean) {
      this.NowQuickMode = quickMode;
    },
    setNowAffixedIntervals(intervals: []) {
      this.NowAffixedIntervals = intervals;
    },
    setSpotLeverageActivate(activate: boolean) {
      this.spotLeverageActivate = activate;
    },
    setSpotLeverageAutoLoan(autoLoan: boolean) {
      this.spotLeverageAutoLoan = autoLoan;
    },
    setAiLeftSelectDisplay(display: boolean) {
      this.AiLeftSelectDisplay = display;
    },
    setAiLeftSelectAiSelect(isAiSelect: boolean) {
      this.AiLeftSelectAiSelect = isAiSelect;
    },
    setExtendChart(tab_id: string, jsonData: string) {
      if (this.ExtendChartMap == undefined) {
        this.ExtendChartMap = {};
      }
      this.ExtendChartMap[tab_id] = jsonData;
    },
    getExtendChart(tab_id: string) {
      if (this.ExtendChartMap == undefined) {
        this.ExtendChartMap = {};
      }
      return this.ExtendChartMap[tab_id];
    },
    loadAssetPageFilterPrefs(userKey: string) {
      return readAssetPageFilterPrefs(userKey);
    },
    persistAssetPageFilterPrefs(
      userKey: string,
      prefs: AssetPageFilterPrefsV1
    ) {
      writeAssetPageFilterPrefs(userKey, prefs);
    },
  },
}) as StoreDefinition<
  "dashboard",
  {
    currentDashBoard: string;
    dashBoardList: DashBoardTab[];
    searchSymbolList: BaseSymbol[];
    symbolsAI?: BaseSymbol[];
    NowChart: string;
    NowChartSaveLoadAdapter: string;
    currentCommandTab: string;
    currentPositionAssetApiIdList: number[];
    NowSyncMode: boolean;
    NowQuickMode: boolean;
    NowAffixedIntervals: [];
    spotLeverageActivate: boolean;
    spotLeverageAutoLoan: boolean;
    astockNameMap: Map<string, AStockSymbolWhite>;
    NowCurrencyType: number;
    NowSyncRatio?: number;
    AiLeftSelectDisplay: boolean;
    AiLeftSelectAiSelect: boolean;

    ExtendChartMap: Record<string, string>;
  },
  {},
  {
    addDashBoard(symbol: string): void;
    removeDashBoard(symbol: string): void;
    replaceDashBorad(oldSymbol: string, newSymbol: string): void;
    initSearchSymbolList(): void;
    searchSymbol(text: string, acType: string): void;
    getTitleByTabId(tab_id: string): string;
    reconcileApiSelectListWithUserApiMap(): void;
    refreshAstockNameMap(): void;
    setSymbolsAI(symbolsMapAI: BaseSymbol[] | undefined): void;
    getSymbolsAI(): BaseSymbol[];
    setCurrentCommandTab(tab: string): void;
    setSyncMode(syncMode: boolean): void;
    setQuickMode(quickMode: boolean): void;
    setNowAffixedIntervals(intervals: string[]): void;
    setSpotLeverageActivate(activate: boolean): void;
    setSpotLeverageAutoLoan(autoLoan: boolean): void;
    setAiLeftSelectDisplay(display: boolean): void;
    setAiLeftSelectAiSelect(select: boolean): void;
    setExtendChart(tab_id: string, jsonData: string): void;
    getExtendChart(tab_id: string): string | undefined;
    loadAssetPageFilterPrefs(
      userKey: string
    ): Partial<AssetPageFilterPrefsV1> | null;
    persistAssetPageFilterPrefs(
      userKey: string,
      prefs: AssetPageFilterPrefsV1
    ): void;
  }
>;

export interface KchartData {
  StudyTemplateJson: string;
  ChartJson: string;
}
